import React from 'react';
import { graphql } from 'gatsby';
import * as styles from './CampaignMasthead.module.css';
import {
    KontentStringValue,
    CTAConfiguration,
    GeneralImages,
    KontentRadioMultiChoice,
    BrandColor,
    KontentStringCodenameValue,
    KontentRichText,
    KontentMedia,
    Statement,
    IconSectionData,
} from '../../../types/KontentProps';
import HealthSection from './HealthSection';
import MediaLogoSection from './MediaLogoSection';

export type Props = CTAConfiguration &
    GeneralImages &
    Statement &
    IconSectionData & {
        kontentItemId?: string;
        component_anchor_name: KontentStringValue;
        cta_show_or_hide: KontentRadioMultiChoice<'show' | 'hide'>;
        description_copy: KontentRichText;
        description_style?: KontentStringCodenameValue;
        description_tag?: KontentStringCodenameValue;
        display_headline: KontentStringValue;
        display_headline_tag?: KontentStringCodenameValue;
        display_sub_headline: KontentStringValue;
        display_sub_headline_tag?: KontentStringCodenameValue;
        general_large___small_image_selection__large: KontentMedia;
        general_large___small_image_selection__small: KontentMedia;
        sub_headline_style?: KontentStringCodenameValue;
        enable_trademark_styling?: KontentRadioMultiChoice<'yes' | 'no'>;
        primary_background_color: BrandColor;
        primary_font_color: BrandColor;
        secondary_background_color: BrandColor;
        secondary_font_color: BrandColor;
        extend_bottom_edge_of_component: KontentRadioMultiChoice<'yes' | 'no'>;
    };

const CampaignMasthead: React.FC<Props> = (props) => {
    const { icon_section_style, kontentItemId } = props;
    const style = icon_section_style?.value?.[0]?.codename;

    return (
        <section
            className={styles.masthead}
            data-kontent-item-id={kontentItemId}
        >
            {style === 'health_benefits' ? (
                <HealthSection {...props} />
            ) : (
                <MediaLogoSection {...props} />
            )}
        </section>
    );
};

export const fragmentCampaignMasthead = graphql`
    fragment CampaignMasthead on kontent_item_component___campaign_masthead {
        id
        elements {
            component_anchor_name {
                value
            }
            description_copy {
                value
            }
            description_style {
                value {
                    codename
                    name
                }
            }
            description_tag {
                value {
                    codename
                    name
                }
            }
            display_headline {
                value
            }
            enable_trademark_styling {
                value {
                    codename
                }
            }
            display_headline_tag {
                value {
                    codename
                    name
                }
            }
            display_sub_headline {
                value
            }
            display_sub_headline_tag {
                value {
                    codename
                    name
                }
            }

            cta_show_or_hide {
                value {
                    name
                    codename
                }
            }
            cta_configuration__cta_label {
                value
            }
            cta_configuration__cta_location {
                ...kontentCtaLocation
            }
            cta_configuration__cta_location_anchor_point {
                value
            }
            cta_configuration__cta_window_action {
                value {
                    codename
                }
            }
            extend_bottom_edge_of_component {
                value {
                    codename
                }
            }
            icon_statements {
                value {
                    ... on kontent_item_media___image {
                        id
                        elements {
                            file {
                                value {
                                    url
                                    width
                                    height
                                    description
                                    name
                                }
                            }
                        }
                    }
                    ... on kontent_item_content_item___statement_with_icon {
                        id
                        elements {
                            display_title {
                                value
                            }
                            icon {
                                value {
                                    ... on kontent_item_media___icon {
                                        id
                                        elements {
                                            icon_svg_file {
                                                value {
                                                    url
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
            general_large___small_image_selection__large {
                ...kontentImage
            }
            general_large___small_image_selection__small {
                ...kontentImage
            }
            sub_headline_style {
                value {
                    name
                    codename
                }
            }
            primary_background_color {
                ...kontentColor
            }
            primary_font_color {
                ...kontentColor
            }
            secondary_background_color {
                ...kontentColor
            }
            secondary_font_color {
                ...kontentColor
            }
            icon_section_title {
                value
            }
            icon_section_style {
                value {
                    codename
                    name
                }
            }
        }
    }
`;

export default CampaignMasthead;
